import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import type { ProductSizeInterface } from '../../../../../../../../Interface/ProductSize';
import QuantityField from '../../../QuantityField/QuantityField';
import type { BasketAddStatus } from '../../useBoothBasket';
import type { LineSheetPrice } from '../../pricing';
import { formatNumber, formatPrice } from '../../pricing';
import styles from '../../AssortmentPanel.module.scss?module';

interface FlavourGridProps {
  item: ProductSizeInterface;
  price: LineSheetPrice;
  /** Entered quantity per flavour (shop id). */
  quantities: Record<number, number>;
  onQuantityChange: (attributeId: number, quantity: number) => void;
  basketUnitsByAttributeId: Map<number, number>;
  status: BasketAddStatus;
  onAdd: (item: ProductSizeInterface) => void;
}

/** Flavour matrix of one item: a quantity per flavour, the running sum and one Add. */
export default function FlavourGrid({
  item,
  price,
  quantities,
  onQuantityChange,
  basketUnitsByAttributeId,
  status,
  onAdd,
}: FlavourGridProps) {
  const flavours = item.product_attributes ?? [];
  const units = flavours.reduce(
    (sum, flavour) => sum + (quantities[flavour.id] ?? 0),
    0
  );
  const isPending = status === 'pending';
  const isAdded = status === 'added';

  if (flavours.length === 0) {
    return <p className={styles.flavoursEmpty}>No flavours listed.</p>;
  }

  return (
    <div className={styles.flavourGrid}>
      <ul className={styles.flavourList}>
        {flavours.map((flavour) => {
          const inBasket = basketUnitsByAttributeId.get(flavour.id) ?? 0;

          return (
            <li key={flavour.id} className={styles.flavourRow}>
              <span className={styles.flavourText}>
                <span className={styles.flavourName}>{flavour.name}</span>
                {inBasket > 0 && (
                  <span className={styles.flavourInBasket}>
                    In basket: {formatNumber(inBasket)}
                  </span>
                )}
              </span>
              <QuantityField
                value={quantities[flavour.id] ?? 0}
                onChange={(quantity) => onQuantityChange(flavour.id, quantity)}
                label={flavour.name}
                disabled={isPending}
              />
            </li>
          );
        })}
      </ul>
      <div className={styles.flavourFooter}>
        <span className={styles.terms}>
          Quantity unrestricted · delivery after the show
        </span>
        <div className={styles.footerActions}>
          <span className={styles.selection} aria-live="polite">
            {formatNumber(units)} {units === 1 ? 'unit' : 'units'} selected
            {units > 0 && price.current !== null && (
              <span className={styles.selectionTotal}>
                {' · '}
                {formatPrice(units * price.current, price.currency)}
              </span>
            )}
          </span>
          <button
            type="button"
            className={styles.addButton}
            data-status={status}
            disabled={isPending || (units === 0 && !isAdded)}
            onClick={() => {
              if (units > 0 && !isPending) {
                onAdd(item);
              }
            }}
          >
            {isAdded ? (
              <>
                <FontAwesomeIcon icon={faCheck} aria-hidden="true" /> Added
              </>
            ) : isPending ? (
              'Adding …'
            ) : (
              'Add to basket'
            )}
          </button>
        </div>
        {status === 'error' && (
          <p className={styles.addError} role="alert">
            Could not add to basket. Please try again.
          </p>
        )}
      </div>
    </div>
  );
}
